import RootPage from "containers/RootPage";
import { REPOSITORY_VEYO, REPOSITORY_VOICE } from "core/consts";
import { isProd } from "core/model/utils/featureFlags";
import RedirectRoot from "dsl/atoms/RedirectRoot";
import lazy from "dsl/atoms/retry";
import LogoutPage from "dsl/ecosystems/LogoutPage";
import PasswordChangePageV2 from "dsl/ecosystems/PasswordChangePageV2";
import AcpAuthPage, { AcpAuthenticate } from "dsl/organisms/AcpAuthentication";
import AcpSealdDebug from "dsl/organisms/AcpSealdDebug";
import { Navigate, Route, Routes } from "react-router-dom";

const CareproviderAcp = lazy(() => import("dsl/ecosystems/CareproviderAcp"));
const BedrockAcpTool = lazy(() => import("dsl/organisms/BedrockACP"));
const AIWorkbench = lazy(() => import("dsl/organisms/AIWorkbench"));
const VoiceSummaryPromptTool = lazy(
  () => import("dsl/organisms/VoiceSummaryPromptTool"),
);
const CompanyPage = lazy(() => import("dsl/ecosystems/AcpCompanyPage"));
const CreateCareprovider = lazy(
  () => import("dsl/ecosystems/AcpDashboard/CreateCareprovider"),
);
const CreateCareseeker = lazy(
  () => import("dsl/ecosystems/AcpDashboard/CreateCareseeker"),
);
const AcpDashboard = lazy(() => import("dsl/ecosystems/AcpDashboard"));
const Chromatic = lazy(() => import("dsl/organisms/Chromatic"));
const PasswordResetPage = lazy(() => import("dsl/organisms/PasswordResetPage"));
const AcpInsurancePage = lazy(() => import("dsl/ecosystems/AcpInsurancePage"));
const Playground = lazy(() => import("dsl/organisms/Playground"));
const InternalAccounts = lazy(() => import("dsl/organisms/InternalAccounts"));
const ManuallyAddedProviders = lazy(
  () => import("dsl/organisms/ManuallyAddedProviders"),
);
const SearchPage = lazy(() => import("dsl/organisms/SearchPage"));
const AccountNavigation = lazy(
  () => import("dsl/organisms/Account/AcpAccountNavigation"),
);
const AccountPage = lazy(() => import("dsl/organisms/Account"));
const AccountEventsPage = lazy(
  () => import("dsl/organisms/Account/AcpAccountEventTimeline"),
);

const ReportPage = lazy(() => import("dsl/organisms/Careseeker/ReportPage"));
const CareseekerNavigation = lazy(
  () => import("dsl/organisms/Careseeker/AcpCareseekerNavigation"),
);
const ProfilePage = lazy(() => import("dsl/ecosystems/CareseekerACP"));
const ProvidersPage = lazy(
  () => import("dsl/organisms/Careseeker/ProvidersPage"),
);
const CareseekerEventsPage = lazy(
  () => import("dsl/organisms/EventTimeline/CareseekerAcpTimeline"),
);
const RulesTableCareseeker = lazy(
  () => import("dsl/ecosystems/CareproviderAcp/RulesTableCareseeker"),
);
const AcpOnpremisePage = lazy(() => import("dsl/organisms/AcpOnpremisePage"));
const AcpSSOPage = lazy(() => import("dsl/organisms/AcpSSOPage"));
const TranslationsPanel = lazy(() => import("dsl/ecosystems/AcpTranslations"));
const EmailTranslationsPanel = lazy(
  () => import("dsl/ecosystems/AcpEmailTranslations"),
);
const TestEmailTool = lazy(() => import("dsl/ecosystems/AcpTestEmail"));
const AcpHome = lazy(() => import("dsl/ecosystems/AcpHome"));
const DebugPage = lazy(() => import("dsl/ecosystems/AcpDebugPage"));

const ConsultantUpdatePage = lazy(
  () => import("dsl/ecosystems/AcpConsultantPage"),
);
const ConsultantCreatePage = lazy(
  () => import("dsl/ecosystems/AcpConsultantPage/Create"),
);
const ForgotPasswordPage = lazy(
  () => import("dsl/ecosystems/ForgotPasswordPage"),
);

export const defaultRedirectPath = "/acp/dashboard";
export const acpAuthPath = "/acp/auth";

export default function AcpRoutes() {
  return (
    <Routes>
      <Route element={<Navigate replace to={defaultRedirectPath} />} index />
      <Route element={<RootPage appName="acp" />}>
        <Route element={<LogoutPage />} path="/logout" />
        <Route path="/acp/*">
          <Route element={<Chromatic />} path="chromatic" />
          <Route element={<DebugPage />} path="info" />
          <Route element={<ForgotPasswordPage />} path="forgot-password" />
          <Route element={<AcpAuthPage />} path="auth" />
          <Route
            element={
              <AcpAuthenticate>
                <AcpDashboard />
              </AcpAuthenticate>
            }
            path="dashboard"
          >
            <Route element={<AcpHome />} index />
            <Route
              element={<PasswordChangePageV2 />}
              path="my-password-change"
            />
            <Route
              element={<TranslationsPanel repo={REPOSITORY_VEYO} />}
              path="translations"
            />
            <Route
              element={<EmailTranslationsPanel />}
              path="emailtranslations"
            />
            <Route
              element={<TranslationsPanel repo={REPOSITORY_VOICE} />}
              path="nativetranslations"
            />
            <Route element={<TestEmailTool />} path="testemail" />
            <Route element={<SearchPage />} path="search" />
            <Route element={<Playground />} path="playground" />
            <Route element={<AcpSealdDebug />} path="sealddebug/:page" />
            <Route element={<InternalAccounts />} path="staff" />
            <Route element={<ManuallyAddedProviders />} path="manuallyadded" />
            <Route path="careprovider">
              <Route
                element={<CareproviderAcp />}
                path=":careproviderId/:page"
              />
              <Route element={<CareproviderAcp />} path=":careproviderId" />
              <Route
                element={<Navigate replace to="/acp/dashboard" />}
                path="careprovider"
              />
            </Route>
            <Route element={<CompanyPage />} path="company/:companyid" />
            <Route
              element={<ConsultantUpdatePage />}
              path="consultant/:consultant_id"
            />
            <Route
              element={<CreateCareprovider />}
              path="create/careprovider"
            />
            <Route element={<CreateCareseeker />} path="create/careseeker" />
            <Route
              element={<ConsultantCreatePage />}
              path="create/consultant"
            />
            <Route element={<AcpInsurancePage />} path="insurance" />
            <Route
              element={<AcpInsurancePage />}
              path="insurance/:insuranceId"
            />
            <Route element={<AccountNavigation />} path="account/:accountId">
              <Route element={<AccountPage />} index />
              <Route element={<AccountPage />} path="profile" />
              <Route element={<AccountEventsPage />} path="timeline" />
            </Route>
            <Route
              element={<CareseekerNavigation />}
              path="careseeker/:careseekerId"
            >
              <Route element={<ProfilePage />} index />
              <Route element={<ProfilePage />} path="profile" />
              <Route element={<ReportPage />} path="report" />
              <Route element={<ProvidersPage />} path="providers" />
              <Route element={<CareseekerEventsPage />} path="timeline" />
              <Route
                element={<RulesTableCareseeker />}
                path="sender-receiver-rules"
              />
              <Route element={<AcpOnpremisePage />} path="onpremise" />
              <Route element={<AcpSSOPage />} path="sso" />
            </Route>
            {!isProd && <Route element={<BedrockAcpTool />} path="ai" />}
            {!isProd && (
              <>
                <Route
                  element={<AIWorkbench />}
                  path="ai-workbench/dataset/:datasetId/item/:itemId"
                />
                <Route
                  element={<AIWorkbench />}
                  path="ai-workbench/dataset/:datasetId"
                />
                <Route element={<AIWorkbench />} path="ai-workbench" />
              </>
            )}
            {!isProd && (
              <Route element={<VoiceSummaryPromptTool />} path="voicetool" />
            )}
          </Route>
          <Route
            element={<PasswordResetPage authRoute={acpAuthPath} />}
            path="reset/:token"
          />
          <Route element={<RedirectRoot to={defaultRedirectPath} />} path="*" />
        </Route>
      </Route>
    </Routes>
  );
}
